import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useMatch } from "react-router-dom";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import {
  Download,
  Eye,
  Filter,
  Globe,
  MapPin,
  Search,
  SlidersHorizontal,
  User,
} from "lucide-react";

import PaginationLayout from "../components/PaginationLayout";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import DownloadfileModal from "../components/common/downloadfileModal";
import henceofrthEnums from "../utils/henceofrthEnums";
import {
  downloadExcelFile,
  ExportColumn,
} from "../components/common/ExportButton";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const bookingExportColumns: ExportColumn[] = [
  { header: "Sr. No.", key: "sr_no", type: "number", width: 8 },
  { header: "Order ID", key: "order_id", type: "text", width: 16 },
  { header: "Passenger Name", key: "user_name", type: "text", width: 18 },
  { header: "Driver Name", key: "driver_name", type: "text", width: 18 },
  { header: "Pick up address", key: "pickup", type: "text", width: 28 },
  { header: "Drop off address", key: "drop", type: "text", width: 28 },
  { header: "Seats", key: "seats", type: "number", width: 10 },
  {
    header: "Price",
    key: "price",
    type: "currency",
    isAmount: true,
    width: 14,
  },
  { header: "Tax", key: "tax", type: "currency", isAmount: true, width: 12 },
  { header: "Date", key: "date", type: "date", width: 18 },
  { header: "Status", key: "status", type: "text", width: 14 },
];

interface driver {
  profile_pic: string;
  name: string;
}
interface userlist {
  profile_pic: string;
  name: string;
}
interface book {
  _id: string;
  booked_seats: number;
  total_price: number;
  tax?: number;
  booking_status: string;
  city_from: string;
  city_to: string;
  created_at: string;
  driver_detail: driver;
  user_detail: userlist;
  pickup?: { lat: number; long: number; address: string };
  dropoff?: { lat: number; long: number; address: string };
  currency?: any;
}

const BookingListing = () => {
  const match = useMatch("/booked-rides/:page");
  const location = useLocation();
  const navigate = useNavigate();
  const limit = 10;
  const { authState, handleFilter, handlePagination, handleError } =
    useContext(GlobalContext);
  const params: any = useParams();
  const query = new URLSearchParams(location.search);

  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<Array<book>>([]);
  const [totalCount, setTotalCount] = useState(0);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const downloadCsv = async (start_date: string, end_date: string) => {
    try {
      let apiRes = await henceforthApi.BookingListing.export(
        moment(start_date).format("DD/MM/YYYY"),
        moment(end_date).format("DD/MM/YYYY")
      );
      let data = apiRes?.data?.recent_bookings || [];

      const rows = data.map((res: any, index: number) => ({
        sr_no: index + 1,
        order_id: res?.order_id || "N/A",
        user_name: res?.user_detail?.name || "N/A",
        driver_name: res?.driver_detail?.name || "N/A",
        pickup: (
          res?.pickup?.address ||
          res?.pickup_address ||
          res?.city_from ||
          res?.address_from ||
          ""
        )
          .split(",")
          .join(" "),
        drop: (
          res?.dropoff?.address ||
          res?.dropoff_address ||
          res?.city_to ||
          res?.address_to ||
          ""
        )
          .split(",")
          .join(" "),
        seats: Number(res?.booked_seats || res?.seats || 0),
        price: Number(res?.total_price || 0),
        tax: Number(res?.tax || 0),
        date: res?.created_at ? Number(res.created_at) : "",
        status: res?.booking_status || res?.status || "N/A",
      }));

      await downloadExcelFile({
        fileName: `booked_rides_${moment().format("YYYY-MM-DD")}`,
        sheetName: "Booked Rides",
        columns: bookingExportColumns,
        data: rows,
      });

      let closeModal = document.getElementById("closeModal");
      if (closeModal) {
        closeModal.click();
      }
    } catch (error: any) {
      console.error("Booked rides export error:", error);
    }
  };

  const init = async () => {
    setLoading(true);
    let q = query.toString();
    try {
      let apiRes = await henceforthApi.BookingListing.getPagnation(
        Number(params.page) - 1,
        q,
        limit
      );
      setTotalCount(apiRes.data.count);
      setPageData(apiRes.data.recent_bookings || []);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setPageData([]);
      setTotalCount(0);
      handleError(error);
    }
  };

  useEffect(() => {
    init();
  }, [
    match?.params.page,
    query.get("search"),
    query.get("status"),
    query.get("sort"),
    query.get("country"),
  ]);

  const handleSelectFilter = (key: string, val: string) => {
    if (val && val !== "ALL") {
      handleFilter(key, val);
    } else {
      handleFilter(key, "");
    }
  };

  const formatCurrency = (amount: number, currency: any) => {
    const formatted = Number(amount || 0).toFixed(2);
    if (currency === henceofrthEnums.CurrencyType.NGN) return `₦ ${formatted}`;
    if (currency === henceofrthEnums.CurrencyType.INR) return `₹ ${formatted}`;
    return `$ ${formatted}`;
  };

  // AG Grid Column Definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        width: 75,
        maxWidth: 80,
        valueGetter: (p) => {
          return (pageNum - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Passenger",
        field: "user_detail.name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const user = p.data?.user_detail;
          const imgSrc =
            user?.profile_pic && user?.profile_pic !== "null"
              ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${user?.profile_pic}`
              : profile_placeholder;
          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={imgSrc}
                alt={user?.name || "Passenger"}
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <span className="font-semibold text-slate-800 text-xs leading-tight hover:text-[#006747] transition-colors truncate">
                {user?.name || "Not Available"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Pickup Address",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          const res = p.data;
          const address =
            res?.pickup?.address || res?.city_from || "Not Available";
          return (
            <div
              className="flex items-center gap-1.5 text-slate-700 truncate text-xs"
              title={address}
            >
              <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="truncate">{address}</span>
            </div>
          );
        },
      },
      {
        headerName: "Dropoff Address",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          const res = p.data;
          const address =
            res?.dropoff?.address || res?.city_to || "Not Available";
          return (
            <div
              className="flex items-center gap-1.5 text-slate-700 truncate text-xs"
              title={address}
            >
              <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0" />
              <span className="truncate">{address}</span>
            </div>
          );
        },
      },
      {
        headerName: "Driver",
        field: "driver_detail.name",
        minWidth: 110,
        width: 120,
        cellRenderer: (p: any) => {
          const driver = p.data?.driver_detail;
          return (
            <div className="flex items-center gap-1.5 text-slate-700 font-medium text-xs">
              <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate">{driver?.name || "Not Available"}</span>
            </div>
          );
        },
      },
      {
        headerName: "Seats",
        field: "booked_seats",
        minWidth: 70,
        width: 80,
        cellRenderer: (p: any) => {
          const seats = p.value || 0;
          return (
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold">
              {seats} {seats === 1 ? "seat" : "seats"}
            </span>
          );
        },
      },
      {
        headerName: "Price",
        field: "total_price",
        minWidth: 85,
        width: 95,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-900 text-xs">
              {formatCurrency(p.value, p.data?.currency)}
            </span>
          );
        },
      },
      {
        headerName: "Date",
        field: "created_at",
        minWidth: 115,
        width: 120,
        valueFormatter: (p) => {
          return p.value
            ? moment(Number(p.value)).format("DD MMM YYYY")
            : "N/A";
        },
      },
      {
        headerName: "Status",
        field: "booking_status",
        minWidth: 130,
        width: 135,
        cellRenderer: (p: any) => {
          const status = p.value as string;
          if (
            status === "COMPLETED" ||
            status === "CONFIRMED" ||
            status === "ACCEPTED"
          ) {
            return <Badge variant="success">{status}</Badge>;
          }
          if (status === "PENDING" || status === "ONGOING") {
            return <Badge variant="warning">{status}</Badge>;
          }
          if (
            status?.startsWith("CANCELLED") ||
            status === "DECLINED" ||
            status === "REJECTED"
          ) {
            return <Badge variant="destructive">{status}</Badge>;
          }
          return (
            <Badge variant="outline">
              {status ? status.replace(/_/g, " ") : "N/A"}
            </Badge>
          );
        },
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 85,
        width: 75,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => {
          const booking = p.data as book;
          return (
            <div className="flex items-center justify-center w-full">
              <Link
                to={`/booking-details/${booking?._id}`}
                title="View Details"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#006747] hover:bg-[#006747]/10 transition-colors"
              >
                <Eye className="w-4 h-4" />
              </Link>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  const currentStatus = query.get("status") || "ALL";
  const currentSort = query.get("sort") || "ALL";
  const currentCountry = query.get("country") || "ALL";

  return (
    <>
      {/* Compact Single Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* Left: Search Bar */}
        <div className="relative w-56 sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            name="search"
            defaultValue={query.get("search") || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleFilter("search", e?.target?.value);
            }}
            placeholder="Search passenger/driver..."
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <div className="w-36">
            <Select
              value={currentStatus}
              onValueChange={(val) => handleSelectFilter("status", val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="ONGOING">Ongoing</SelectItem>
                <SelectItem value="DECLINED">Declined</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort Time Filter */}
          <div className="w-36">
            <Select
              value={currentSort}
              onValueChange={(val) => handleSelectFilter("sort", val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="Sort Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Time</SelectItem>
                <SelectItem value="1">Newest</SelectItem>
                <SelectItem value="2">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Country Filter */}
          <div className="w-32">
            <Select
              value={currentCountry}
              onValueChange={(val) => handleSelectFilter("country", val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Countries</SelectItem>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
                <SelectItem value="IN">India</SelectItem>
                <SelectItem value="NG">Nigeria</SelectItem>
                <SelectItem value="ROW">Rest of the World</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Export Button */}
          <Button
            variant="default"
            size="sm"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            className="h-9 px-4 text-sm gap-2 shadow-xs"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={pageData}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No bookings found for this filter"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={pageData}
          page={Number(params.page)}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      <DownloadfileModal data={downloadCsv} />
    </>
  );
};

export default BookingListing;